import { useState, useEffect, useRef, type FormEvent } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import { toast } from "sonner"
import { Mail, User as UserIcon, ArrowRight, Dumbbell } from "lucide-react"
import { useAuth } from "@/contexts/AuthContext"
import { useAnalytics } from "@/hooks/useAnalytics"
import { detectInstalled } from "@/hooks/useInstallPrompt"
import { initialAuthRedirect, resetPassword } from "@/lib/supabase"
import { nextFromAuthSearch } from "@/lib/trialLink"
import {
  MIN_PASSWORD_LENGTH,
  MIN_PASSWORD_HINT,
  afterPasswordChange,
  clearPending,
  clearResetRequest,
  isFromApp,
  isResetUrl,
  loadPending,
  loadResetRequest,
  recoveryScreen,
  saveResetRequest,
  type ResetRequest,
} from "@/lib/passwordRecovery"
import { AuthCard, AuthInput, FieldHint, PasswordInput } from "@/components/AuthUi"
import {
  LinkExpiredScreen,
  NewPasswordScreen,
  PasswordChangedScreen,
  ResetRequestNotice,
} from "@/components/PasswordRecoveryScreens"

type Mode = "login" | "signup"

const SIGNIN_TIMEOUT = 20000 // 20s — défaut, signIn ne devrait jamais prendre plus

/**
 * Enveloppe une promesse avec un timeout. Si la promesse ne résout pas à temps,
 * rejette avec un message d'erreur clair. Évite que le bouton "Connexion..."
 * reste bloqué indéfiniment si le SDK hang.
 */
function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeoutPromise = new Promise<T>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error(`${label} — délai dépassé (${ms / 1000}s). Vérifie ta connexion.`)),
      ms
    )
  })
  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timer) clearTimeout(timer)
  })
}

const pulseStyle = `
@keyframes btn-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(6,182,212,0.45); }
  50% { box-shadow: 0 0 0 10px rgba(6,182,212,0); }
}
.pulse-btn { animation: btn-pulse 2.2s ease-in-out infinite; }
@keyframes float-bg {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(30px, -20px) scale(1.1); }
}
.float-bg { animation: float-bg 12s ease-in-out infinite; }
`

export function AuthPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, signIn, signUp, isAuthenticated, hasWizard, loading: authLoading, wizardLoading } = useAuth()
  const { track } = useAnalytics()

  // Demande de lien faite depuis cet appareil il y a moins d'une heure : email pré-rempli,
  // encadré « Regarde ta boîte mail » toujours affiché (l'app a pu être fermée entre-temps).
  const [resetRequest, setResetRequest] = useState<ResetRequest | null>(loadResetRequest)

  const [mode, setMode] = useState<Mode>("login")
  const [firstName, setFirstName] = useState("")
  const [email, setEmail] = useState(() => resetRequest?.email ?? "")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [resetting, setResetting] = useState(false)

  // ── Mot de passe oublié : quel écran (voir lib/passwordRecovery.ts) ──
  const [pending, setPending] = useState(loadPending)
  /** Le client a quitté l'écran « lien expiré » : l'erreur de l'adresse ne compte plus. */
  const [linkDismissed, setLinkDismissed] = useState(false)
  /** Session de récupération refusée au moment d'enregistrer. */
  const [sessionLost, setSessionLost] = useState(false)
  /** Mot de passe changé, lien demandé depuis l'app installée : écran « C'est fait ». */
  const [changed, setChanged] = useState(false)

  const screen = changed
    ? "changed"
    : sessionLost
      ? "link-expired"
      : recoveryScreen({
          redirect: linkDismissed ? { kind: "none" } : initialAuthRedirect,
          isResetUrl: isResetUrl(location.pathname, location.search),
          pending,
          userId: user?.id ?? null,
        })

  // Redirection auto si déjà connecté — attend aussi que wizardLoading soit false
  // pour éviter de naviguer avec un hasWizard obsolète (sinon flash wizard/dashboard).
  // Jamais pendant un écran « mot de passe oublié » : le lien de l'email CONNECTE, et
  // c'est justement ce renvoi qui empêchait de choisir un nouveau mot de passe.
  //
  // `?next=` : la page d'où vient le client, s'il y a été renvoyé faute d'être connecté
  // (le lien « Voir mon bilan » de l'email de J8). Liste fermée de pages, voir trialLink.ts.
  const next = nextFromAuthSearch(location.search)
  useEffect(() => {
    if (authLoading || wizardLoading || screen) return
    if (isAuthenticated) {
      if (hasWizard) navigate(next ?? "/dashboard", { replace: true })
      else navigate("/", { replace: true })
    }
  }, [authLoading, wizardLoading, screen, isAuthenticated, hasWizard, navigate, next])

  const trackedScreen = useRef<string | null>(null)
  useEffect(() => {
    if ((screen !== "link-expired" && screen !== "other-account") || trackedScreen.current === screen) return
    trackedScreen.current = screen
    track(screen === "link-expired" ? "password_reset_link_expired" : "password_reset_other_account")
  }, [screen, track])

  /** Le lien venait de l'app installée (ou on y est) : la suite doit y ramener. */
  const fromApp = () => detectInstalled() || pending?.fromApp === true || isFromApp(location.search)

  const leaveRecovery = () => {
    clearPending()
    setPending(null)
    setSessionLost(false)
    setLinkDismissed(true)
    // Retire `?reset=1…` et le `#error…` de l'adresse ; connecté, l'effet ci-dessus
    // enchaîne sur l'accueil.
    navigate("/auth", { replace: true })
  }

  const onPasswordChanged = () => {
    const viaApp = pending?.fromApp === true
    clearPending()
    clearResetRequest()
    setPending(null)
    track("password_reset_completed", { from_app: viaApp })
    if (afterPasswordChange({ fromApp: viaApp, standalone: detectInstalled() }) === "back-to-app") {
      setChanged(true)
      return
    }
    toast.success("Mot de passe changé", {
      description: "C'est celui-ci que tu utiliseras à ta prochaine connexion.",
    })
    navigate("/dashboard", { replace: true })
  }

  const sendResetLink = async (to: string): Promise<string | null> => {
    const viaApp = fromApp()
    const { error } = await resetPassword(to, viaApp)
    if (error) return error
    setResetRequest(saveResetRequest(to.trim().toLowerCase(), viaApp))
    track("password_reset_requested", { from_app: viaApp })
    return null
  }

  const canSubmit = () => {
    if (!email.trim() || !password) return false
    if (password.length < MIN_PASSWORD_LENGTH) return false
    if (mode === "signup" && !firstName.trim()) return false
    return true
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!canSubmit() || submitting) return

    setSubmitting(true)
    try {
      if (mode === "login") {
        const result = await withTimeout(signIn(email, password), SIGNIN_TIMEOUT, "Connexion")
        if (result.error) {
          toast.error("Erreur de connexion", { description: result.error })
          return
        }
        clearResetRequest()
        clearPending()
        toast.success("Connecté !", { description: "Bienvenue." })
      } else {
        const result = await withTimeout(
          signUp(email, password, firstName),
          SIGNIN_TIMEOUT,
          "Inscription"
        )
        if (result.error) {
          toast.error("Erreur d'inscription", { description: result.error })
          return
        }
        if (!result.session) {
          toast.success("Compte créé !", {
            description: "Vérifie ta boîte mail pour confirmer ton adresse.",
          })
          setMode("login")
        } else {
          toast.success("Bienvenue !", { description: "Ton compte a été créé." })
        }
      }
    } catch (err) {
      // Timeout ou erreur réseau inattendue
      console.error("[AuthPage] submit error:", err)
      toast.error("Erreur", {
        description: err instanceof Error ? err.message : "Échec de l'opération.",
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleResetPassword = async () => {
    if (!email.trim()) {
      toast.error("Email requis", {
        description: "Saisis ton email puis clique sur mot de passe oublié.",
      })
      return
    }
    setResetting(true)
    try {
      const error = await sendResetLink(email)
      if (error) toast.error("Lien non envoyé", { description: error })
    } finally {
      setResetting(false)
    }
  }

  const switchMode = (m: Mode) => {
    setMode(m)
    setPassword("")
  }

  return (
    <div className="min-h-screen bg-[#060a13] cursor-default relative overflow-hidden flex items-center justify-center px-4 py-8">
      <style>{pulseStyle}</style>

      <div className="fixed inset-0 z-0 pointer-events-none">
        <img src="/dashboard-bg.png" alt="" className="w-full h-full object-cover opacity-[0.05]" />
        <div
          className="float-bg absolute top-[-10%] left-[-10%] w-[400px] h-[400px] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(6,182,212,0.12), transparent 70%)", filter: "blur(40px)" }}
        />
        <div
          className="float-bg absolute bottom-[-10%] right-[-10%] w-[400px] h-[400px] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(3,105,161,0.10), transparent 70%)", filter: "blur(40px)", animationDelay: "4s" }}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="relative z-10 w-full max-w-[420px]"
      >
        <div className="flex flex-col items-center mb-8">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3"
            style={{
              background: "linear-gradient(135deg, rgba(6,182,212,0.18), rgba(6,182,212,0.06))",
              border: "1px solid rgba(6,182,212,0.25)",
              boxShadow: "0 0 20px rgba(6,182,212,0.15)",
            }}
          >
            <Dumbbell className="w-7 h-7 text-cyan-400" strokeWidth={2.2} />
          </div>
          <h1 className="text-[24px] font-bold text-white tracking-tight">Ton coaching</h1>
          <p className="text-[13px] text-gray-500 mt-1">Entraînement sur mesure</p>
        </div>

        {screen === "new-password" ? (
          <AuthCard>
            <NewPasswordScreen
              email={user?.email ?? null}
              onChanged={onPasswordChanged}
              onSessionLost={() => {
                clearPending()
                setPending(null)
                setSessionLost(true)
              }}
            />
          </AuthCard>
        ) : screen === "changed" ? (
          <AuthCard>
            <PasswordChangedScreen onContinue={() => navigate("/dashboard", { replace: true })} />
          </AuthCard>
        ) : screen === "link-expired" || screen === "other-account" ? (
          <AuthCard>
            <LinkExpiredScreen
              reason={screen === "other-account" ? "other-account" : "expired"}
              // Jamais l'adresse du compte connecté : rien ne dit que le lien était pour lui.
              // Seulement celle d'une demande faite depuis cet appareil.
              initialEmail={resetRequest?.email ?? ""}
              isAuthenticated={isAuthenticated}
              onSend={sendResetLink}
              onLeave={leaveRecovery}
            />
          </AuthCard>
        ) : (
          <>
            <AuthCard>
              <div
                className="relative grid grid-cols-2 p-1 rounded-xl mb-6"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(30,41,59,0.3)" }}
              >
                <motion.div
                  className="absolute inset-y-1 rounded-lg"
                  style={{
                    width: "calc(50% - 4px)",
                    background: "linear-gradient(135deg, rgba(6,182,212,0.15), rgba(6,182,212,0.08))",
                    border: "1px solid rgba(6,182,212,0.25)",
                  }}
                  animate={{ x: mode === "login" ? 4 : "calc(100% + 4px)" }}
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                />
                <button
                  type="button"
                  onClick={() => switchMode("login")}
                  className="relative z-10 py-2.5 text-[13px] font-semibold cursor-pointer transition-colors"
                  style={{ color: mode === "login" ? "#06b6d4" : "#94a3b8" }}
                >
                  Connexion
                </button>
                <button
                  type="button"
                  onClick={() => switchMode("signup")}
                  className="relative z-10 py-2.5 text-[13px] font-semibold cursor-pointer transition-colors"
                  style={{ color: mode === "signup" ? "#06b6d4" : "#94a3b8" }}
                >
                  Inscription
                </button>
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={mode}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                  className="mb-5"
                >
                  <h2 className="text-[20px] font-bold text-white">
                    {mode === "login" ? "Bon retour !" : "Crée ton compte"}
                  </h2>
                  <p className="text-[13px] text-gray-500 mt-1">
                    {mode === "login"
                      ? next?.startsWith("/fin-essai")
                        ? "Connecte-toi pour voir ton bilan des 7 jours."
                        : "Connecte-toi pour accéder à ton espace."
                      : "Quelques secondes pour rejoindre ton coaching."}
                  </p>
                </motion.div>
              </AnimatePresence>

              <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                <AnimatePresence initial={false}>
                  {mode === "signup" && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <AuthInput
                        icon={UserIcon}
                        type="text"
                        placeholder="Prénom"
                        value={firstName}
                        onChange={setFirstName}
                        autoComplete="given-name"
                      />
                    </motion.div>
                  )}
                </AnimatePresence>

                <AuthInput
                  icon={Mail}
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={setEmail}
                  autoComplete="email"
                  required
                />

                <div className="relative">
                  <PasswordInput
                    placeholder="Mot de passe"
                    value={password}
                    onChange={setPassword}
                    autoComplete={mode === "login" ? "current-password" : "new-password"}
                    visible={showPassword}
                    onToggleVisible={() => setShowPassword((s) => !s)}
                  />
                  {mode === "signup" && password.length > 0 && password.length < MIN_PASSWORD_LENGTH && (
                    <FieldHint tone="warn">{MIN_PASSWORD_HINT}</FieldHint>
                  )}
                </div>

                {mode === "login" && (
                  <button
                    type="button"
                    onClick={handleResetPassword}
                    disabled={resetting}
                    className="text-[12px] text-gray-500 hover:text-cyan-400 transition-colors text-right cursor-pointer -mt-1 disabled:opacity-50"
                  >
                    {resetting ? "Envoi…" : "Mot de passe oublié ?"}
                  </button>
                )}

                <button
                  type="submit"
                  disabled={!canSubmit() || submitting}
                  className={`pulse-btn w-full py-3.5 rounded-xl text-[15px] font-bold text-white flex items-center justify-center gap-2 cursor-pointer transition-all active:scale-[0.98] mt-2 ${
                    !canSubmit() || submitting ? "opacity-50 !animate-none" : ""
                  }`}
                  style={{ background: "linear-gradient(135deg, #0369a1, #0891b2, #06b6d4)" }}
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                      <span>{mode === "login" ? "Connexion…" : "Création…"}</span>
                    </>
                  ) : (
                    <>
                      <span>{mode === "login" ? "Se connecter" : "Créer mon compte"}</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>

              {mode === "login" && resetRequest && <ResetRequestNotice request={resetRequest} />}

              {mode === "signup" && (
                <p className="text-[11px] text-gray-600 text-center mt-4 leading-relaxed">
                  En créant un compte, tu acceptes nos{" "}
                  <a href="#" className="text-gray-500 underline cursor-pointer">CGU</a> et notre{" "}
                  <a href="#" className="text-gray-500 underline cursor-pointer">politique de confidentialité</a>.
                </p>
              )}
            </AuthCard>

            <p className="text-[12px] text-gray-600 text-center mt-6">
              {mode === "login" ? "Pas encore de compte ?" : "Déjà inscrit ?"}{" "}
              <button
                onClick={() => switchMode(mode === "login" ? "signup" : "login")}
                className="text-cyan-400 font-semibold cursor-pointer hover:underline"
              >
                {mode === "login" ? "Créer un compte" : "Se connecter"}
              </button>
            </p>
          </>
        )}
      </motion.div>
    </div>
  )
}
